// ============================================================================
// Corporate Facts in a side sheet — "Edit data" from a note or report
// ============================================================================
// The data a document prints (Additional information figures, the related
// party register…) is entered in Corporate Facts. From the document, the
// same Corporate Facts workspace opens here on the right section, so the
// auditor keeps their place; on save the document refreshes its tables.

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';
import { fsaEngagementService } from 'api/services/fsa/fsaEngagementService';
import type { FsaEngagementSettings } from 'api/types/fsa/engagementSettings.types';
import FsaCorporateFactsPane, { type FactsSection } from './FsaCorporateFactsPane';

interface Props {
  open: boolean;
  engagementId: string;
  section: FactsSection;
  onClose: () => void;
  /** After a save — the host refreshes what it prints. */
  onSaved?: () => void;
}

const CorporateFactsSheet: React.FC<Props> = ({ open, engagementId, section, onClose, onSaved }) => {
  const [settings, setSettings] = useState<FsaEngagementSettings | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    fsaEngagementService.getSettings(engagementId).then(r => setSettings(r.data ?? null)).catch(() => setSettings(null));
  }, [open, engagementId]);

  const close = useCallback(() => {
    // eslint-disable-next-line no-alert
    if (dirty && !window.confirm('Corporate Facts has unsaved changes. Close and discard them?')) return;
    setDirty(false);
    onClose();
  }, [dirty, onClose]);

  if (!open) return null;
  return createPortal(
    <div style={overlayStyle} onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
      <aside style={sheetStyle} role="dialog" aria-modal="true" aria-label="Corporate Facts">
        <div style={sheetHeaderStyle}>
          <strong style={{ flex: 1 }}>Corporate Facts</strong>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Saved here, printed by the document you came from</span>
          <button type="button" onClick={close} className="modal-close" aria-label="Close" title="Close">
            <IconSystem.x size={18} />
          </button>
        </div>
        {settings ? (
          <FsaCorporateFactsPane
            engagementId={engagementId}
            clientId={settings.clientId ? String(settings.clientId) : null}
            periodStart={settings.periodStart ?? null}
            financialYearEnd={settings.financialYearEnd}
            initialSection={section}
            onDirtyChange={setDirty}
            onSaved={onSaved}
          />
        ) : (
          <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>Loading…</div>
        )}
      </aside>
    </div>,
    document.body,
  );
};

// Above the note / report window (10050), below the action menus (10100) its lists use.
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.35)', zIndex: 10080, display: 'flex', justifyContent: 'flex-end',
};
const sheetStyle: React.CSSProperties = {
  width: 'min(1180px, 94vw)', height: '100vh', background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column',
  boxShadow: '-12px 0 40px rgba(15, 23, 42, 0.25)',
};
const sheetHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border-color)', flexShrink: 0,
};

export default CorporateFactsSheet;
